/**
 * **Which statuses an inventory item can be set to, and what each is called**
 * (VALE-75 / [INVDRAW-4]).
 *
 * Extracted from `StatusDropdownField`'s JSX in
 * `components/inventory/inventory-form-card/InventoryFormCard.tsx`, where it was
 * a hand-written run of `<SelectItem>`s. The attribute drawer needs the same
 * vocabulary as a `select` row's `options`, and re-typing it there would give one
 * fact two definitions — including the two rules below, both of which encode a
 * **backend refusal** rather than a display preference, so a drifted copy would
 * offer the reader a status the API rejects (root `CLAUDE.md` § "Bind to the
 * source of truth").
 *
 * Two omissions carry real weight, and neither is obvious from the enum:
 *
 * · **A liability is never offered `settled`** (LF-3098). It reaches that state
 *   only by being included in a distribution that is marked distributed; the
 *   backend rejects a direct write with
 *   `LIABILITY_SETTLED_VIA_DISTRIBUTION_ONLY`. An item already settled shows it
 *   as a **disabled** option so the reader can see the state they are in, and
 *   {@link isSettledLiabilityLocked} then locks the control outright — only
 *   `reverseDistributed` on the parent distribution flips it back.
 * · **`resolved` is offered to every liability but only to share and real-estate
 *   assets** (LF-4655). On a liability it means a debt cleared *outside* a Vale
 *   distribution — paid before death, or from an estate account directly —
 *   which is distinct from `settled` above.
 *
 * `confirmed` appears in {@link InventoryItemStatus} and in no option list: it is
 * written by workflow, not chosen.
 */

import type { InventoryItem, InventoryItemStatus } from './types';

/**
 * The display name for one item status.
 *
 * `InventoryStatusBadge` binds its own item-status labels to this rather than
 * keeping a second copy; it still carries the uppercase legacy keys and the
 * institution workflow stages, which are not item statuses and have no entry
 * here.
 */
export const INVENTORY_STATUS_LABELS: Record<InventoryItemStatus, string> = {
  draft: 'Draft',
  notified: 'Notified',
  identified: 'Identified',
  'not-held': 'Not Held',
  collected: 'Collected',
  resolved: 'Resolved',
  archived: 'Archived',
  confirmed: 'Confirmed',
  settled: 'Settled',
  'not-owed': 'Not Owed'
};

export function inventoryStatusLabel(status: string): string {
  return (
    INVENTORY_STATUS_LABELS[status as InventoryItemStatus] ??
    // An unknown status renders as itself rather than as an empty pill — a
    // status the FE has not been taught yet is still a fact about the record.
    status
  );
}

/**
 * The two asset types that can be `resolved` — a holding disposed of outside a
 * collection (LF-4655). Shares and real estate; every other asset type reaches a
 * terminal state through `collected` or `not-held`.
 */
const RESOLVABLE_ASSET_TYPES = new Set([
  'srn-shares',
  'hin-shares',
  'real-estate'
]);

/** Whether an asset of this type can be `resolved` — see {@link RESOLVABLE_ASSET_TYPES}. */
export function isResolvableAssetType(typeSlug: string | undefined): boolean {
  return !!typeSlug && RESOLVABLE_ASSET_TYPES.has(typeSlug);
}

export interface InventoryStatusOption {
  value: InventoryItemStatus;
  label: string;
  /** Shown so the reader can see the state they are in, but not selectable. */
  disabled?: boolean;
}

/**
 * A settled liability's status control is locked (LF-3098) — see this module's
 * header. Exported so the dropdown and the attribute row apply one rule.
 */
export function isSettledLiabilityLocked({
  category,
  currentStatus
}: {
  category: InventoryItem['category'] | undefined;
  currentStatus: string;
}): boolean {
  return category === 'liability' && currentStatus === 'settled';
}

export function inventoryStatusOptions({
  category,
  resolvableAsset = false,
  currentStatus
}: {
  category: InventoryItem['category'] | undefined;
  /**
   * Whether an *asset* may also be `resolved` — {@link isResolvableAssetType}
   * from a type slug. Ignored for a liability, which is always offered it.
   */
  resolvableAsset?: boolean;
  /** Decides only whether a disabled `settled` row is shown. */
  currentStatus: string;
}): InventoryStatusOption[] {
  const option = (
    value: InventoryItemStatus,
    disabled?: boolean
  ): InventoryStatusOption => ({
    value,
    label: INVENTORY_STATUS_LABELS[value],
    ...(disabled ? { disabled } : {})
  });

  const options: InventoryStatusOption[] = [option('draft')];

  if (category === 'liability') {
    options.push(option('notified'), option('identified'));
    if (currentStatus === 'settled') {
      options.push({
        value: 'settled',
        label: 'Settled (via distribution)',
        disabled: true
      });
    }
    options.push(option('not-owed'), option('resolved'));
  } else {
    options.push(
      option('notified'),
      option('identified'),
      option('not-held'),
      option('collected')
    );
    if (resolvableAsset) {
      options.push(option('resolved'));
    }
  }

  options.push(option('archived'));
  return options;
}
